import React from "react";
import { Activity, Dumbbell, Footprints, HeartPulse, Brain, Sparkles } from "lucide-react";

const SERVICE_IMG_1 = "https://customer-assets.emergentagent.com/job_senior-balance-rehab/artifacts/p8jfle42_ChatGPT%20Image%20Jun%2027%2C%202026%2C%2008_44_28%20AM.png";
const SERVICE_IMG_2 = "https://customer-assets.emergentagent.com/job_senior-balance-rehab/artifacts/poy13dim_ChatGPT%20Image%20Jun%2027%2C%202026%2C%2010_07_21%20AM.png";

const services = [
  {
    icon: Footprints,
    color: "text-brand-terracotta",
    title: "Falls Prevention",
    desc: "Targeted balance, gait and reactive training to dramatically reduce fall risk.",
  },
  {
    icon: Dumbbell,
    color: "text-brand-teal",
    title: "Clinical Strength Training",
    desc: "Progressive resistance work that rebuilds muscle, bone density and stamina.",
  },
  {
    icon: Activity,
    color: "text-brand-gold",
    title: "Mobility & Flexibility",
    desc: "Restore joint range, ease stiffness and move with comfort.",
  },
  {
    icon: HeartPulse,
    color: "text-brand-terracotta",
    title: "Injury Recovery",
    desc: "Post-op, fracture and soft tissue rehab guided by chartered physios.",
  },
  {
    icon: Brain,
    color: "text-brand-teal",
    title: "Healthy Ageing",
    desc: "Long-term programmes that keep you independent, active, confident and safe.",
  },
  {
    icon: Sparkles,
    color: "text-brand-gold",
    title: "1:1 Personal Physio",
    desc: "Individual sessions tailored to your abilities, needs and goals.",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      className="py-20 md:py-32 px-6 md:px-12 bg-brand-cream"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-16">
          <span className="text-sm font-semibold tracking-[0.2em] uppercase text-brand-terracotta" data-testid="services-eyebrow">
            What we offer
          </span>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-brand-blue mt-4 leading-tight" data-testid="services-headline">
            Programmes built around real lives, real bodies.
          </h2>
          <p className="text-lg text-slate-700 leading-relaxed mt-6">
            Every session is designed, delivered and supervised by a registered professional. We combine clinical assessment
            with progressive training so you don&apos;t just recover — you keep getting stronger.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid lg:grid-cols-12 gap-6">
          {/* Large feature image */}
          <div className="lg:col-span-5 lg:row-span-2 rounded-3xl overflow-hidden relative group min-h-[320px]" data-testid="services-feature-1">
            <img src={SERVICE_IMG_1} alt="Senior gentleman doing supervised dumbbell training at Reading Physio" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
          </div>

          {services.slice(0, 4).map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className={`lg:col-span-${idx === 0 || idx === 3 ? "4" : "3"} bg-white rounded-3xl p-7 border border-brand-clay hover:border-brand-blue/40 hover:shadow-md transition-all duration-300 group`}
                data-testid={`service-card-${idx + 1}`}
              >
                <Icon size={32} strokeWidth={1.5} className={`${s.color} mb-5`} />
                <h3 className="font-display text-xl md:text-2xl text-brand-blue mb-2">{s.title}</h3>
                <p className="text-base text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}

          {services.slice(4).map((s, idx) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                className="lg:col-span-3 bg-brand-sand rounded-3xl p-7 border border-brand-clay hover:border-brand-blue/40 hover:shadow-md transition-all duration-300"
                data-testid={`service-card-${idx + 5}`}
              >
                <Icon size={32} strokeWidth={1.5} className={`${s.color} mb-5`} />
                <h3 className="font-display text-xl md:text-2xl text-brand-blue mb-2">{s.title}</h3>
                <p className="text-base text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            );
          })}

          <div className="lg:col-span-6 rounded-3xl overflow-hidden relative min-h-[260px]" data-testid="services-feature-2">
            <img src={SERVICE_IMG_2} alt="Senior balance training during a home visit with Reading Physio" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-brand-cream/95 via-brand-cream/60 to-transparent" />
            <div className="relative h-full flex flex-col justify-center p-8 max-w-md">
              <h3 className="font-display text-2xl md:text-3xl text-brand-blue leading-tight">Strength is medicine after 60.</h3>
              <p className="mt-3 text-base text-slate-700">Clinical programming. Light weights. Real results — without ever leaving your comfort zone.</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
